import React, { createContext, useContext, useEffect } from 'react';
import { useSettings } from './SettingsContext';

interface ThemeContextType {
  applyTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getSetting } = useSettings();

  const calculateDerivedColors = (hue: number, saturation: number, lightness: number) => {
    return {
      // Primary color
      primary: `${hue} ${saturation}% ${lightness}%`,
      // Foreground (lighter for text on primary)
      primaryForeground: `${hue} ${Math.min(saturation + 5, 100)}% ${Math.min(lightness + 45, 95)}%`,
      // Border (same hue, lower saturation)
      border: `${hue} ${Math.max(saturation - 35, 10)}% ${lightness + 5}%`,
      // Ring (same as primary)
      ring: `${hue} ${saturation}% ${lightness}%`,
    };
  };

  const applyTheme = () => {
    const customEnabled = getSetting('theme_custom_enabled') === 'true';
    
    if (customEnabled) {
      const hue = parseInt(getSetting('theme_primary_hue') || '142');
      const saturation = parseInt(getSetting('theme_primary_saturation') || '76');
      const lightness = parseInt(getSetting('theme_primary_lightness') || '30');

      // Validate ranges
      const validHue = Math.max(0, Math.min(360, hue));
      const validSat = Math.max(0, Math.min(100, saturation));
      const validLight = Math.max(0, Math.min(100, lightness));

      const colors = calculateDerivedColors(validHue, validSat, validLight);

      // Apply custom colors to CSS variables
      document.documentElement.style.setProperty('--primary', colors.primary);
      document.documentElement.style.setProperty('--primary-foreground', colors.primaryForeground);
      document.documentElement.style.setProperty('--border', colors.border);
      document.documentElement.style.setProperty('--ring', colors.ring);
    } else {
      // Remove custom styles to revert to CSS defaults
      document.documentElement.style.removeProperty('--primary');
      document.documentElement.style.removeProperty('--primary-foreground');
      document.documentElement.style.removeProperty('--border');
      document.documentElement.style.removeProperty('--ring');
    }
  };

  useEffect(() => {
    applyTheme();
  }, [getSetting('theme_custom_enabled'), getSetting('theme_primary_hue'), getSetting('theme_primary_saturation'), getSetting('theme_primary_lightness')]);

  return (
    <ThemeContext.Provider value={{ applyTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
